function foldTurkishText(value) {
  return String(value || "")
    .replace(/İ/g, "i")
    .replace(/I/g, "i")
    .replace(/ı/g, "i")
    .replace(/Ğ/g, "g")
    .replace(/ğ/g, "g")
    .replace(/Ü/g, "u")
    .replace(/ü/g, "u")
    .replace(/Ş/g, "s")
    .replace(/ş/g, "s")
    .replace(/Ö/g, "o")
    .replace(/ö/g, "o")
    .replace(/Ç/g, "c")
    .replace(/ç/g, "c");
}

export const compactHeader = (value) =>
  foldTurkishText(String(value || ""))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");

export const cellString = (value) => {
  if (value == null) return "";
  return String(value).replace(/\s+/g, " ").trim();
};

const HEADER_RULES = [
  { re: /parent.*phone|parent.*tel|veli.*tel|veli.*phone|veli.*cep|guardianphone|parent1phone|parent2phone/, kind: "parentPhone" },
  { re: /parent.*mail|veli.*posta|veli.*mail|guardianemail/, kind: "parentEmail" },
  { re: /parent|veli|guardian|anneadi|babaadi|mother|father/, kind: "parentName" },
  { re: /toplama|sabahservis|morningroute|pickuproute|pickupservice|^sabah$|^morning$/, kind: "pickupRoute" },
  { re: /dagitma|aksamservis|^aksam$|eveningroute|dropoffroute|dropoffservice|^afternoon$|^evening$/, kind: "dropoffRoute" },
  { re: /plaka|plate|surucu|sofor|driver|hostes|rehber|kaptan/, kind: "ignore" },
  { re: /guzergah|routename|servisadi|servisismi|^route$|^servis$|^hat$|hatadi|vehicleroute|arac$/, kind: "pickupRoute" },
  { re: /birakil|getoff|dropoff|addressoff|teslimadres|adresinis/, kind: "addressGetOff" },
  { re: /address|adres|street|sokak|mahalle|cadde|geton|homeaddr|evadres/, kind: "addressGetOn" },
  { re: /okulnumara|ogrencino|studentno|studentnumber|studentid|identifier|okulno/, kind: "identifier" },
  { re: /studentname|passengername|ogrenciad|fullname|adsoyad|adisoyad|namesurname|ogrenci.*soyad/, kind: "name" },
  { re: /okulismi|okuladi|schoolname|campusname/, kind: "school" },
  { re: /^(school|okul|campus|kurum|institution)$/, kind: "school" },
  { re: /grade|class|sinif|level|sube/, kind: "grade" },
  { re: /email|eposta/, kind: "email" },
  { re: /phone|telefon|mobile|cep|gsm|whatsapp/, kind: "phone" },
  { re: /firstname|givenname|^ad$/, kind: "firstName" },
  { re: /lastname|surname|soyad|familyname/, kind: "lastName" },
  { re: /student|passenger|ogrenci|cocuk|child|rider/, kind: "name" },
  { re: /name|isim/, kind: "name" },
];

export const classifyHeader = (header) => {
  const compact = compactHeader(header);
  if (!compact) return null;
  if ((compact.includes("okul") || compact.includes("school")) && /(numara|number|no|id)$/.test(compact)) {
    return "identifier";
  }
  if ((compact.includes("okul") || compact.includes("school")) && compact.includes("adres")) {
    return null;
  }
  for (const rule of HEADER_RULES) {
    if (rule.re.test(compact)) return rule.kind === "ignore" ? null : rule.kind;
  }
  if ((compact.includes("okul") || compact.includes("school")) && !/sorumlu|telefon|mail|saat/.test(compact)) {
    return "school";
  }
  return null;
};

export const rosterHeaderSignals = (headers = []) => {
  const kinds = new Set(headers.map((header) => classifyHeader(header)).filter(Boolean));
  return {
    name: kinds.has("name") || (kinds.has("firstName") && kinds.has("lastName")),
    address: kinds.has("addressGetOn") || kinds.has("addressGetOff"),
    parent: kinds.has("parentName") || kinds.has("parentPhone") || kinds.has("parentEmail"),
    school: kinds.has("school"),
    route: kinds.has("pickupRoute") || kinds.has("dropoffRoute"),
    any: kinds.has("addressGetOn") || kinds.has("addressGetOff") || kinds.has("parentName") || kinds.has("parentPhone") || kinds.has("school"),
  };
};

export const foldTurkish = (value) => foldTurkishText(value);

export const nameKey = (value) =>
  foldTurkishText(cellString(value))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const looksLikePersonName = (value) => {
  const text = cellString(value);
  if (!text || text.length < 2 || text.length > 90) return false;
  if (/@/.test(text) || /^\+?\d[\d\s().-]{6,}$/.test(text)) return false;
  if (/\d{4,}/.test(text)) return false;
  const letters = text.replace(/[^A-Za-zÀ-ÿĞğİıÖöŞşÜüÇç]/g, "");
  if (letters.length < 2) return false;
  const tokens = text.split(/\s+/).filter(Boolean);
  return tokens.length >= 2 || letters.length >= 5;
};

const looksLikeAddress = (value) => {
  const text = cellString(value);
  if (text.length < 8) return false;
  return /mah|sok|cad|apt|apart|no[:.]?\s*\d|street|avenue|road|blvd|antalya|istanbul|ankara|\d/.test(text.toLowerCase());
};

const looksLikePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

const scoreColumn = (rows, index, pred) => {
  if (!rows.length) return 0;
  let hits = 0;
  let nonempty = 0;
  for (const row of rows) {
    const value = cellString(row[index]);
    if (!value) continue;
    nonempty += 1;
    if (pred(value)) hits += 1;
  }
  if (nonempty < Math.max(2, Math.floor(rows.length * 0.25))) return 0;
  return hits / nonempty;
};

const unusedIndexes = (headers, roles) => {
  const used = new Set();
  Object.entries(roles).forEach(([key, value]) => {
    if (key === "unmapped") return;
    if (typeof value === "number") used.add(value);
    if (Array.isArray(value)) value.forEach((index) => used.add(index));
  });
  return headers.map((_, index) => index).filter((index) => !used.has(index));
};

const pickBest = (rows, indexes, pred, minScore) => {
  let best = { index: null, score: 0 };
  for (const index of indexes) {
    const score = scoreColumn(rows, index, pred);
    if (score > best.score) best = { index, score };
  }
  return best.score >= minScore ? best.index : null;
};

export const mapHeaders = (headers = [], rows = []) => {
  const roles = {
    name: null,
    firstName: null,
    lastName: null,
    school: null,
    addressGetOn: null,
    addressGetOff: null,
    phone: null,
    email: null,
    grade: null,
    identifier: null,
    pickupRoute: null,
    dropoffRoute: null,
    parentNames: [],
    parentPhones: [],
    parentEmails: [],
    unmapped: [],
  };

  headers.forEach((header, index) => {
    const kind = classifyHeader(header);
    if (!kind) {
      roles.unmapped.push(header || `Column ${index + 1}`);
      return;
    }
    if (kind === "parentName") roles.parentNames.push(index);
    else if (kind === "parentPhone") roles.parentPhones.push(index);
    else if (kind === "parentEmail") roles.parentEmails.push(index);
    else if (roles[kind] == null) roles[kind] = index;
    else roles.unmapped.push(header || `Column ${index + 1}`);
  });

  const unused = unusedIndexes(headers, roles);
  if (roles.name == null && roles.firstName == null) {
    const chosen = pickBest(rows, unused, looksLikePersonName, 0.55);
    if (chosen != null) {
      roles.name = chosen;
      unused.splice(unused.indexOf(chosen), 1);
    }
  }
  if (roles.addressGetOn == null) {
    const chosen = pickBest(rows, unused, looksLikeAddress, 0.4);
    if (chosen != null) {
      roles.addressGetOn = chosen;
      unused.splice(unused.indexOf(chosen), 1);
    }
  }
  if (roles.parentPhones.length === 0 && roles.phone == null) {
    const chosen = pickBest(rows, unused, looksLikePhone, 0.5);
    if (chosen != null) roles.parentPhones.push(chosen);
  }

  return roles;
};

const at = (row, index) => {
  if (index == null) return "";
  return cellString(row[index]);
};

export const extractPassengers = (headers = [], rows = []) => {
  const roles = mapHeaders(headers, rows);
  const passengers = [];

  rows.forEach((row, index) => {
    const name = at(row, roles.name) || [at(row, roles.firstName), at(row, roles.lastName)].filter(Boolean).join(" ");
    const addressGetOn = at(row, roles.addressGetOn);
    const parentCount = Math.max(roles.parentNames.length, roles.parentPhones.length, roles.parentEmails.length);
    const parents = [];
    for (let parentIndex = 0; parentIndex < parentCount; parentIndex += 1) {
      const parent = {
        name: at(row, roles.parentNames[parentIndex]),
        phone: at(row, roles.parentPhones[parentIndex]),
        email: at(row, roles.parentEmails[parentIndex]),
      };
      if (parent.name || parent.phone || parent.email) parents.push(parent);
    }
    if (!parents.length && (at(row, roles.phone) || at(row, roles.email))) {
      parents.push({ name: "", phone: at(row, roles.phone), email: at(row, roles.email) });
    }
    if (!name && !addressGetOn && !parents.length) return;
    passengers.push({
      rowNumber: index + 2,
      name,
      identifier: at(row, roles.identifier),
      grade: at(row, roles.grade),
      schoolName: at(row, roles.school),
      addressGetOn,
      addressGetOff: at(row, roles.addressGetOff) || addressGetOn,
      phone: at(row, roles.phone),
      email: at(row, roles.email),
      pickupRouteName: at(row, roles.pickupRoute),
      dropoffRouteName: at(row, roles.dropoffRoute),
      parents,
    });
  });

  return { roles, passengers };
};

const ROUTE_HEADER_RULES = [
  { re: /telefon|phone|gsm|cep/, kind: "phone" },
  { re: /plaka|plate|licence|license|vehicle(no|number|id)?$/, kind: "plate" },
  { re: /surucu|sofor|driver|captain|kaptan/, kind: "driver" },
  { re: /hostes|rehber|attendant|assistant|yardimci/, kind: "attendant" },
  { re: /tip|type|yon|direction/, kind: "type" },
  { re: /bitis|end|stop|varis|arrival/, kind: "endTime" },
  { re: /baslangic|start|kalkis|departure|saat|time/, kind: "startTime" },
  { re: /kapasite|capacity|koltuk|seat/, kind: "capacity" },
  { re: /okul|school/, kind: "school" },
  { re: /guzergah|route|servis|hat$|hatadi|line/, kind: "name" },
];

export const classifyRouteHeader = (header) => {
  const compact = compactHeader(header);
  if (!compact) return null;
  for (const rule of ROUTE_HEADER_RULES) {
    if (rule.re.test(compact)) return rule.kind;
  }
  return null;
};

const looksLikePlate = (value) => {
  const text = cellString(value).toUpperCase().replace(/\s+/g, "");
  return /^\d{2}[A-Z]{1,3}\d{2,4}$/.test(text) || /^[A-Z0-9-]{5,10}$/.test(text);
};

export const directionFromText = (value) => {
  const compact = compactHeader(value);
  if (!compact) return null;
  if (/dagitma|aksam|dropoff|drop|evening|afternoon|birak|teslim|donus|^pm$/.test(compact)) return "dropoff";
  if (/toplama|sabah|pickup|pick|morning|alis|gidis|^am$/.test(compact)) return "pickup";
  return null;
};

export const normalizeClock = (value) => {
  const text = cellString(value);
  const match = /^(\d{1,2})[:.](\d{2})/.exec(text);
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return "";
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// One row per service *and direction* in the file; rows with the same name become one service
// with up to two legs, each keeping its own times, plate and driver.
export const extractRouteList = (headers = [], rows = []) => {
  const roles = {
    name: null,
    driver: null,
    plate: null,
    phone: null,
    attendant: null,
    capacity: null,
    school: null,
    type: null,
    startTime: null,
    endTime: null,
  };
  headers.forEach((header, index) => {
    const kind = classifyRouteHeader(header);
    if (kind && roles[kind] == null) roles[kind] = index;
  });
  if (roles.plate == null) {
    const guess = pickBest(rows, unusedIndexes(headers, roles), looksLikePlate, 0.6);
    if (guess != null) roles.plate = guess;
  }
  if (roles.name == null) {
    const candidates = unusedIndexes(headers, roles);
    roles.name = candidates.length ? candidates[0] : null;
  }
  const routes = [];
  const byKey = new Map();
  rows.forEach((row, index) => {
    const name = at(row, roles.name);
    if (!name) return;
    const key = compactHeader(name);
    if (!key) return;
    const kindText = at(row, roles.type);
    const direction = directionFromText(kindText) || directionFromText(name);
    const leg = {
      startTime: normalizeClock(at(row, roles.startTime)),
      endTime: normalizeClock(at(row, roles.endTime)),
      plate: at(row, roles.plate),
      driver: at(row, roles.driver),
      driverPhone: at(row, roles.phone),
      attendant: at(row, roles.attendant),
      rowNumber: index + 2,
    };
    let route = byKey.get(key);
    if (!route) {
      route = {
        rowNumber: index + 2,
        name,
        driver: leg.driver,
        driverPhone: leg.driverPhone,
        plate: leg.plate,
        attendant: leg.attendant,
        capacity: at(row, roles.capacity),
        schoolName: at(row, roles.school),
        routeKind: kindText,
        legs: { pickup: null, dropoff: null },
        pickup: false,
        dropoff: false,
      };
      byKey.set(key, route);
      routes.push(route);
    }
    (direction ? [direction] : ["pickup", "dropoff"]).forEach((target) => {
      if (!route.legs[target]) route.legs[target] = leg;
      route[target] = true;
    });
    if (!route.driver) route.driver = leg.driver;
    if (!route.plate) route.plate = leg.plate;
    if (!route.driverPhone) route.driverPhone = leg.driverPhone;
  });
  return { roles, routes };
};

export const extractSchoolFromKeyValue = (rows) => {
  let name = "";
  let address = "";
  (rows || []).forEach((row) => {
    const label = compactHeader(row?.[0]);
    const value = cellString(row?.[1]);
    if (!label || !value) return;
    if (/sorumlu|telefon|mail|saat|baslangic|bitis/.test(label)) return;
    if (!name && (/okulismi|okuladi|schoolname|^okul$/.test(label) || (label.includes("okul") && /isim|adi|name/.test(label)))) {
      name = value;
      return;
    }
    if (!address && (/okuladres|schooladdress/.test(label) || (label.includes("adres") && label.includes("okul")))) {
      address = value;
    }
  });
  return { name, address };
};

export const looksLikeKeyValue = (rows) => {
  if (!rows || rows.length < 2 || rows.length > 40) return false;
  const pairs = rows.filter((row) => cellString(row[0]) && cellString(row[1]));
  if (pairs.length < 2) return false;
  const wide = rows.filter((row) => row.filter((cell) => cellString(cell)).length > 3);
  if (wide.length > 1) return false;
  const labels = pairs.filter((row) => /okul|adres|saat|telefon|isim|school|name|address|mail|sorumlu/i.test(String(row[0])));
  return labels.length >= 2;
};
